import * as React from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/ui/icon';

export interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

export interface FilterBarLabels {
  filters?: string;
  clearFilters?: string;
  activeFilters?: string;
}

export interface FilterBarProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  filters?: FilterOption[];
  activeFilters?: string[];
  onFilterToggle?: (filterId: string) => void;
  onClearFilters?: () => void;
  actions?: React.ReactNode;
  labels?: FilterBarLabels;
  filtersContent?: React.ReactNode;
  activeFiltersContent?: React.ReactNode;
  className?: string;
}

export function FilterBar({
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Cerca...',
  filters = [],
  activeFilters = [],
  onFilterToggle,
  onClearFilters,
  actions,
  labels,
  filtersContent,
  activeFiltersContent,
  className,
}: FilterBarProps) {
  const hasActiveFilters = activeFilters.length > 0;
  const resolvedLabels: Required<FilterBarLabels> = {
    filters: 'Filtri:',
    clearFilters: 'Pulisci filtri',
    activeFilters: 'Filtri attivi:',
    ...(labels ?? {}),
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search and actions row */}
      <div className="flex items-center justify-between gap-4">
        {/* Search input */}
        <div className="relative flex-1 min-w-[240px]">
          <Icon
            name="search"
            className="absolute left-3 top-1/2 text-base -translate-y-1/2 text-text-tertiary"
          />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Actions */}
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Filters row */}
      {(filtersContent || filters.length > 0) && (
        <div className="flex items-center justify-between gap-4">
          {/* Filter options */}
          <div className="flex items-center gap-2 flex-wrap">
            {filtersContent ? (
              filtersContent
            ) : (
              <>
                <div className="flex items-center gap-1 text-sm text-text-secondary">
                  <Icon name="filter_list" className="text-base" />
                  <span className="font-medium">{resolvedLabels.filters}</span>
                </div>

                {filters.map((filter) => {
                  const isActive = activeFilters.includes(filter.id);

                  return (
                    <Button
                      key={filter.id}
                      variant={isActive ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => onFilterToggle?.(filter.id)}
                      className="h-8"
                    >
                      <span>{filter.label}</span>
                      {filter.count !== undefined && (
                        <Badge
                          variant={isActive ? 'secondary' : 'default'}
                          className="ml-1 h-4 min-w-4 text-xs"
                        >
                          {filter.count}
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </>
            )}
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-text-secondary hover:text-text-primary"
            >
              <Icon name="close" className="text-base mr-1" />
              {resolvedLabels.clearFilters}
            </Button>
          )}
        </div>
      )}

      {/* Active filters summary */}
      {hasActiveFilters && !activeFiltersContent && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <span>{resolvedLabels.activeFilters}</span>
          <div className="flex gap-1">
            {activeFilters.map((filterId) => {
              const filter = filters.find((f) => f.id === filterId);
              if (!filter) return null;

              return (
                <Badge
                  key={filterId}
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => onFilterToggle?.(filterId)}
                >
                  {filter.label}
                  <Icon name="close" className="ml-1 text-sm" />
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {hasActiveFilters && activeFiltersContent ? activeFiltersContent : null}
    </div>
  );
}
